import { Text } from '@chakra-ui/layout'
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  Tag,
  TagLabel,
  Text as ChakraText,
  chakra,
  Tooltip,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'

import { getFieldsMapping, staticFieldNames } from '../../../utils/mediaLibrary'
import type { CustomMetadataResponse, FieldIdentifier } from '../../interfaces'

export interface AddFilterMenuProps {
  customMetadataFields: CustomMetadataResponse[]
  page: 'media-library' | 'media-collection'
  scope: 'inner' | 'outer'
  onAddFilter(fieldIdentifier: FieldIdentifier): void
}

const AddFilterMenu = (props: AddFilterMenuProps): React.ReactElement => {
  const { onAddFilter, scope, customMetadataFields } = props
  const { t } = useTranslation()

  const button = useMemo(
    () =>
      scope === 'outer' ? (
        <Tooltip label="Add new group">
          <MenuButton
            paddingLeft="0.25em"
            textAlign="start"
            as={Text}
            color={colors.appClearBlue}
            fontWeight="medium"
            cursor="pointer"
          >
            <chakra.div marginTop="0.5em">
              {t('plus_add_filter', '+ Add Filter')}
            </chakra.div>
          </MenuButton>
        </Tooltip>
      ) : (
        <Tooltip label="Add filter in same group">
          <MenuButton cursor="pointer">
            <Tag
              marginTop="0.5em"
              marginLeft="0.125em"
              paddingY="0.5em"
              paddingX="0.75em"
              height="2rem"
              borderLeftRadius="0"
              _hover={{ bgColor: colors.appBlueyGrey }}
            >
              <TagLabel color={colors.appSlate}>
                <ChakraText fontWeight="medium" color={colors.appSlate}>
                  {t('plus_sign', '+')}
                </ChakraText>
              </TagLabel>
            </Tag>
          </MenuButton>
        </Tooltip>
      ),
    [scope],
  )

  return (
    <Menu placement="auto-start">
      {button}
      <MenuList overflow="auto" maxHeight="21.5rem">
        {props.page === 'media-collection'
          ? staticFieldNames.mediaCollectionDefaultFilters.map(fieldName => (
              <MenuItem
                key={fieldName}
                onClick={() => void onAddFilter({ fieldName })}
              >
                {getFieldsMapping({ fieldName }).displayText}
              </MenuItem>
            ))
          : null}
        {props.page === 'media-library'
          ? staticFieldNames.otherFields.map(fieldName => (
              <MenuItem
                key={fieldName}
                onClick={() => void onAddFilter({ fieldName })}
              >
                {getFieldsMapping({ fieldName }).displayText}
              </MenuItem>
            ))
          : null}
        {props.page === 'media-library' ? (
          <MenuGroup title="Embedded Metadata">
            {staticFieldNames.embeddedMetadataFields.map(fieldName => (
              <MenuItem
                key={fieldName}
                onClick={() => void onAddFilter({ fieldName })}
              >
                {getFieldsMapping({ fieldName }).displayText}
              </MenuItem>
            ))}
          </MenuGroup>
        ) : null}
        {customMetadataFields.length > 0 && (
          <MenuGroup title="Custom Metadata">
            {customMetadataFields.map(customMetadataField => (
              <MenuItem
                key={customMetadataField.name}
                onClick={() => void onAddFilter({ customMetadataField })}
              >
                {customMetadataField.label}
              </MenuItem>
            ))}
          </MenuGroup>
        )}
      </MenuList>
    </Menu>
  )
}

export default AddFilterMenu
